import { BlobWithFilename, DocumentError, isBlobWithFilename } from 'apiConnector/document/document';
import { useGenerateDocument } from 'components/common-form/sidePanel/docToolbar/documents/DocumentButton.helper';
import { DocumentFormat } from 'epic/common-form/Documentation';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { DocumentType } from 'models/documents/DocumentType';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { documentDownloaded, documentDownloadFailed, documentDownloadStart } from 'state/document/actions';
import { selectIsLanguageDocDownloading, selectLanguageDocErrors } from 'state/document/selector';
import { selectEssentialFormInfos, selectEssentialsFormQuoteId } from 'state/essential/infos/selectors';
import { selectQuoteResult } from 'state/quotes/results/results.selectors';
import {
  getFormProductSubType,
  getFormProductType,
  getIsUserInternal,
  selectEnrichedCombinedDocTitle,
} from 'state/selectors';
import { downloadFile } from 'utils/documents/downloadDocument';

export function useDownloadEssentialFormDoc(documentType: DocumentType, language: string) {
  const tabId = useAssertedActiveTabId();
  const quoteQuery = useAppSelector(state => selectEssentialFormInfos(state, tabId)?.quoteQuery);
  const quoteId = useAppSelector(state => selectEssentialsFormQuoteId(state, tabId));
  const externalQuoteId = useAppSelector(state => selectQuoteResult(state, quoteId)?.quoteExternalId);
  const productType = useAppSelector(state => getFormProductType(state, tabId));
  const productSubtype = useAppSelector(state => getFormProductSubType(state, tabId));
  const isLoading = useAppSelector(state => selectIsLanguageDocDownloading(state, tabId, documentType, language));
  const documentTitle = useAppSelector(state => selectEnrichedCombinedDocTitle(state, tabId));
  const isInternal = useAppSelector(state => getIsUserInternal(state));
  const [error] = useAppSelector(state => selectLanguageDocErrors(state, tabId, documentType, language));

  const errorContext = useMemo(
    () => ({
      productSubtype,
      shouldDisplayErrorDetails: isInternal,
      shouldDisplayStandardErrorMessage: !isInternal,
      shouldUseRejectionCode: !isInternal,
    }),
    [productSubtype, isInternal],
  );

  const generateDocument = useGenerateDocument(
    quoteQuery,
    documentType,
    productSubtype,
    externalQuoteId,
    'Indicative',
    productType,
  );

  const dispatch = useDispatch();

  const onError = (locale: string, error: DocumentError, documentFormat: DocumentFormat) => {
    dispatch(documentDownloadFailed(tabId, locale, error, documentType, documentFormat));
  };

  const onSuccess = (locale: string, documentFormat: DocumentFormat) => {
    dispatch(documentDownloaded(tabId, locale, documentType, documentFormat));
  };

  const onDownloadStart = (locale: string, documentFormat: DocumentFormat) => {
    dispatch(documentDownloadStart(tabId, locale, documentType, documentFormat));
  };

  const handleDownloadResult = (result: Blob | BlobWithFilename, fallbackFilename?: string) => {
    if (isBlobWithFilename(result)) {
      return downloadFile(result.blob, result.filename ?? fallbackFilename);
    }
    return downloadFile(result, fallbackFilename);
  };

  const onDownload = (format?: DocumentFormat) => {
    if (!format) {
      return;
    }

    const fileName = documentTitle ?? `${documentType}-${language}`;
    logAnalytics.trackEvent('Primary market essential', 'Request for documentation', documentType);

    onDownloadStart(language, format);

    generateDocument(language, format)
      .then(result => handleDownloadResult(result, fileName))
      .then(() => onSuccess(language, format))
      .catch(err => onError(language, err, format));
  };

  return { onDownload, isLoading, error, errorContext };
}
